/**
 * A campaign is one event for one audience, so it needs one clock the whole team
 * reads the same way — an operator in Manila and one in Berlin agreeing a launch
 * banner goes up "at 14:30" only works if 14:30 names the same instant to both.
 * Every announcement date and time in this admin is therefore read and written in
 * Central European Time, whatever zone the browser happens to sit in.
 *
 * The BE is unaffected: it stores UTC instants either way. Every conversion and
 * every campaign-zone rendering lives in this module — the form's wall-clock
 * strings on one side, the stored instant on the other.
 *
 * Moving this constant is not a one-line change: the zone is also written out in
 * operator-facing copy (`schedule-section.tsx`, `content-section.tsx`,
 * `help-page.tsx`) and `campaignZoneAbbreviationAt` names CET/CEST specifically.
 * Those do not fail a test if this moves, so they have to move with it by hand.
 */
export const CAMPAIGN_TIME_ZONE = 'Europe/Berlin'

const HOUR_MS = 60 * 60 * 1000

/** The calendar fields an operator actually types, with no zone attached. */
export type WallClock = {
  year: number
  month: number
  day: number
  hours: number
  minutes: number
}

/** The date half of a wall clock, for answers that are about a day rather than a moment. */
export type CalendarDay = Pick<WallClock, 'year' | 'month' | 'day'>

const partsFormatters = new Map<string | undefined, Intl.DateTimeFormat>()

/**
 * `hourCycle: 'h23'` rather than the locale default, because `h24` renders
 * midnight as hour 24 and `Date.UTC` rolls that into the next day — a one-day
 * error visible only on campaigns starting at 00:00. Do not drop it.
 */
const zonedPartsFormatter = (timeZone: string | undefined): Intl.DateTimeFormat => {
  const cached = partsFormatters.get(timeZone)
  if (cached !== undefined) {
    return cached
  }

  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  })
  partsFormatters.set(timeZone, formatter)

  return formatter
}

const CAMPAIGN_PARTS_FORMATTER = zonedPartsFormatter(CAMPAIGN_TIME_ZONE)

type ZonedParts = WallClock & { seconds: number }

const readZonedParts = (formatter: Intl.DateTimeFormat, instantMs: number): ZonedParts => {
  const parts = formatter.formatToParts(instantMs)
  const read = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? Number.NaN)

  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hours: read('hour'),
    minutes: read('minute'),
    seconds: read('second'),
  }
}

/** The campaign zone's wall clock at an instant, as numbers rather than a string. */
export const instantToCampaignWallClock = (instantMs: number): WallClock =>
  readZonedParts(CAMPAIGN_PARTS_FORMATTER, instantMs)

/**
 * How far a zone runs ahead of UTC at a given instant — the only thing separating
 * a wall clock from an instant, and the thing that moves twice a year.
 *
 * Derived by re-reading the instant's own zoned parts as if they were UTC, so it
 * follows the IANA database through every past and future transition rather than
 * assuming today's rule held in 1996 or will still hold in 2035.
 */
const zoneOffsetMsAt = (
  instantMs: number,
  formatter: Intl.DateTimeFormat = CAMPAIGN_PARTS_FORMATTER,
): number => {
  const parts = readZonedParts(formatter, instantMs)
  const asIfUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hours,
    parts.minutes,
    parts.seconds,
  )

  // Offsets are whole minutes, so the instant's own milliseconds would only add
  // noise to a subtraction meant to yield a clean offset.
  return asIfUtc - Math.floor(instantMs / 1000) * 1000
}

/**
 * Two passes, because the offset needed to place a wall clock is itself a function
 * of the answer: the first reads the offset near the target, the second confirms it
 * still holds once applied.
 *
 * They disagree only across a spring-forward gap, where the typed wall clock never
 * existed: the hour is skipped whole, so a typed 02:30 and a typed 03:30 name the
 * same instant. An autumn wall clock that happens twice resolves to its second,
 * standard-time occurrence.
 */
export const campaignWallClockToInstant = (wall: WallClock): number => {
  const asIfUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hours, wall.minutes)
  if (Number.isNaN(asIfUtc)) {
    return Number.NaN
  }

  const nearOffset = zoneOffsetMsAt(asIfUtc)
  const candidate = asIfUtc - nearOffset
  const settledOffset = zoneOffsetMsAt(candidate)

  return settledOffset === nearOffset ? candidate : asIfUtc - settledOffset
}

/**
 * Midnight of a campaign-zone day — but only when that whole day keeps one offset.
 * Takes the day alone: an hour passed in would be discarded, and a signature that
 * accepts one reads as though it were honoured.
 *
 * Null on a changeover day, because such a day has no single answer: on 2026-03-29
 * midnight is CET and 15:00 is CEST, and on 2026-10-25 it is the other way round.
 * Naming the zone from midnight would label an afternoon start with the
 * abbreviation that expired earlier that same day — the exact hour of drift the
 * label exists to prevent, on the one day of the year it is most likely to bite.
 * (The two days are symmetric at the UTC instant, 01:00Z, but not on the local
 * clock: CET ends at 02:00 in March, CEST at 03:00 in October.) Sampling a
 * different hour only moves which half is wrong; refusing to name it is the honest
 * answer until the operator supplies a time.
 */
export const campaignDayMidnightIfUniform = (day: CalendarDay): number | null => {
  const dayStart = campaignWallClockToInstant({ ...day, hours: 0, minutes: 0 })
  const dayEnd = campaignWallClockToInstant({ ...day, hours: 23, minutes: 59 })
  if (Number.isNaN(dayStart) || Number.isNaN(dayEnd)) {
    return null
  }

  return zoneOffsetMsAt(dayStart) === zoneOffsetMsAt(dayEnd) ? dayStart : null
}

/**
 * `CET` or `CEST`, derived from the offset rather than from `Intl`'s short zone
 * name: that name is locale-data dependent and several runtimes render Berlin as
 * `GMT+2`, which is the very ambiguity this label exists to remove. The offset is
 * the definition — Central European Time is UTC+1, its summer form UTC+2 — so
 * reading the name back off the offset stays correct even if the EU stops shifting.
 */
export const campaignZoneAbbreviationAt = (instantMs: number): 'CET' | 'CEST' | null => {
  if (Number.isNaN(instantMs)) {
    return null
  }

  return zoneOffsetMsAt(instantMs) === 2 * HOUR_MS ? 'CEST' : 'CET'
}

/**
 * One shape for every campaign-zone moment the operator reads, because the list's
 * Window column and the reader-zone hint are meant to be comparable at a glance —
 * two option lists in two files drift the moment either is edited.
 */
const READABLE_MOMENT: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
}

const CAMPAIGN_WINDOW_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  ...READABLE_MOMENT,
  timeZone: CAMPAIGN_TIME_ZONE,
})

/**
 * `"2 Jul 2026, 16:30"` — an instant on the campaign clock, for the list's Window
 * column. Lives here rather than in `announcement-schedule.ts` so the zone has one
 * owner: a formatter built next to the column it serves is a second place the zone
 * can silently stop being Berlin.
 */
export const formatCampaignWindow = (instantMs: number): string =>
  CAMPAIGN_WINDOW_FORMATTER.format(instantMs)

const hintFormatters = new Map<string | undefined, Intl.DateTimeFormat>()

const readerHintFormatter = (timeZone: string | undefined): Intl.DateTimeFormat => {
  const cached = hintFormatters.get(timeZone)
  if (cached !== undefined) {
    return cached
  }

  const formatter = new Intl.DateTimeFormat('en-GB', {
    ...READABLE_MOMENT,
    timeZone,
    timeZoneName: 'short',
  })
  hintFormatters.set(timeZone, formatter)

  return formatter
}

/**
 * `"2 Jul 2026, 08:30 GMT-4"` — the same instant on the reader's own clock, for an
 * operator who has to translate a CET schedule into the day they are actually
 * living. Null when their zone keeps the same clock as the campaign zone, which is
 * the common case here: the line would otherwise repeat the CET one straight back.
 *
 * `timeZone` defaults to the runtime's own; only tests pass it, because the suite
 * pins TZ=UTC and a hint asserted against a real offset needs a real zone.
 */
export const formatInReaderZone = (instantMs: number, timeZone?: string): string | null => {
  if (Number.isNaN(instantMs)) {
    return null
  }

  const readerFormatter = zonedPartsFormatter(timeZone)
  if (zoneOffsetMsAt(instantMs, readerFormatter) === zoneOffsetMsAt(instantMs)) {
    return null
  }

  return readerHintFormatter(timeZone).format(instantMs)
}
